import type { GenParams, Rng, Term } from './types';

/**
 * "Millionaire-plausible" wrong answers, in priority tiers modelling real mistakes:
 *  1. one sign flipped · off-by-one in B (±a) · wrong A (swapped with another value from the set)
 *  2. off-by-two in B (±2a) · digit transposition · ±10
 *  3. random filler within ±15 %
 * All candidates are distinct, ≥ 1, ≠ value and inside [0.5·value, 1.6·value + 4].
 */
export function distractors(terms: readonly Term[], value: number, p: GenParams, rng: Rng): number[] {
  const need = p.options - 1;
  const lo = Math.max(1, Math.floor(value * 0.5));
  const hi = Math.ceil(value * 1.6) + 4;
  const ok = (c: number): boolean => Number.isInteger(c) && c >= lo && c <= hi && c !== value;

  const t1: number[] = [];
  const t2: number[] = [];
  for (let i = 0; i < terms.length; i++) {
    const t = terms[i]!;
    const s = t.sign === '-' ? -1 : 1;
    if (i > 0) t1.push(value - 2 * s * t.a * t.b);
    t1.push(value + t.a, value - t.a);
    for (const a of p.a) if (a !== t.a) t1.push(value + s * (a - t.a) * t.b);
    t2.push(value + 2 * t.a, value - 2 * t.a);
  }
  const str = String(value);
  for (let i = 0; i < str.length - 1; i++) {
    const arr = str.split('');
    [arr[i], arr[i + 1]] = [arr[i + 1]!, arr[i]!];
    t2.push(Number(arr.join('')));
  }
  t2.push(value + 10, value - 10);

  const out: number[] = [];
  const take = (list: number[]): void => {
    for (const c of rng.shuffle(list)) if (out.length < need && ok(c) && !out.includes(c)) out.push(c);
  };
  take(t1);
  take(t2);
  let guard = 0;
  while (out.length < need && guard++ < 500) {
    const c = value + rng.pick([-1, 1]) * rng.int(1, Math.max(2, Math.ceil(value * 0.15)));
    if (ok(c) && !out.includes(c)) out.push(c);
  }
  // Only reachable for tiny values where the window has < need integers; widen once.
  let widen = 1;
  while (out.length < need) {
    const c = value + widen++;
    if (!out.includes(c)) out.push(c);
  }
  return out;
}
